<template>
  <div class="mutual-fund-drag-calculator">
    <div class="calculator-grid">
      <Card class="input-card">
        <template #title>
          <h3>Mutual Fund Drag Analyzer</h3>
        </template>
        <template #content>
          <div class="form-fields">
            <div class="field">
              <label>Expense Ratio (%)</label>
              <InputNumber v-model="formData.expenseRatioPercent" :min="0" :max="5" :step="0.01" class="input-field" />
            </div>
            <div class="field">
              <label>Annual Turnover (%)</label>
              <InputNumber v-model="formData.turnoverPercent" :min="0" :max="200" :step="1" class="input-field" />
              <small class="help-text">Higher turnover can increase trading costs and tax drag</small>
            </div>
            <div class="field">
              <label>Gross Fund Return (% p.a.)</label>
              <InputNumber v-model="formData.grossReturnPercent" :min="-50" :max="100" :step="0.1" class="input-field" />
            </div>
            <div class="field">
              <label>Horizon (Years)</label>
              <InputNumber v-model="formData.durationYears" :min="1" :max="50" :step="1" class="input-field" />
            </div>

            <ANButton label="Calculate" severity="primary" size="large" @click="calculate" :disabled="!isValidInput" class="calculate-btn" />
          </div>
        </template>
      </Card>

      <Card v-if="result" class="result-card">
        <template #title>
          <h3>Impact Summary</h3>
        </template>
        <template #content>
          <div class="results">
            <div class="result-item">
              <div>Estimated Annual Drag</div>
              <div class="result-value">{{ result.annualDragPercent }}%</div>
            </div>
            <div class="result-item">
              <div>Net Expected Return</div>
              <div class="result-value">{{ result.netReturnPercent }}%</div>
            </div>
            <div class="result-item">
              <div>Growth of ₹1 (Gross)</div>
              <div class="result-value">₹{{ result.fvGross }}</div>
            </div>
            <div class="result-item">
              <div>Growth of ₹1 (Net)</div>
              <div class="result-value">₹{{ result.fvNet }}</div>
            </div>
            <div class="result-item">
              <div>Loss vs Gross over horizon</div>
              <div class="result-value">{{ result.lostPercentOverHorizon }}%</div>
            </div>
          </div>
        </template>
      </Card>
    </div>

    <Card v-if="result && chartData.labels" class="chart-card">
      <template #title>
        <h3>Growth Visualization (₹1 invested)</h3>
      </template>
      <template #content>
        <div class="chart-container">
          <Chart type="line" :data="chartData" :options="chartOptions" class="chart" />
        </div>
      </template>
    </Card>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { Card } from 'primevue'
import InputNumber from 'primevue/inputnumber'
import ANButton from '@/components/common/AnButton.vue'
import Chart from 'primevue/chart'

// Simple mutual fund drag calculator (expense + turnover impact)
const formData = ref({
  expenseRatioPercent: 1.8,
  turnoverPercent: 50,
  grossReturnPercent: 12,
  durationYears: 10
})

const result = ref(null)
const chartData = ref({})
const chartOptions = ref({})

const isValidInput = computed(() => {
  return formData.value.expenseRatioPercent >= 0 && formData.value.turnoverPercent >= 0 && formData.value.durationYears > 0
})

const calculate = () => {
  if (!isValidInput.value) return

  // Estimate annual drag: expense ratio + (turnover% * 0.5%) as a proxy for transaction costs/impact
  const expense = formData.value.expenseRatioPercent / 100
  const turnoverImpact = (formData.value.turnoverPercent / 100) * 0.5 / 100 // convert percent of turnover into fractional drag

  const annualDragFraction = expense + turnoverImpact
  const grossR = formData.value.grossReturnPercent / 100
  const netReturn = grossR - annualDragFraction

  // Future value of 1 rupee invested
  const fvGross = (Math.pow(1 + grossR, formData.value.durationYears))
  const fvNet = (Math.pow(1 + netReturn, formData.value.durationYears))

  result.value = {
    annualDragPercent: (annualDragFraction * 100).toFixed(3),
    netReturnPercent: (netReturn * 100).toFixed(2),
    fvGross: Math.round(fvGross * 100) / 100,
    fvNet: Math.round(fvNet * 100) / 100,
    lostPercentOverHorizon: (((fvGross - fvNet) / fvGross) * 100).toFixed(2)
  }

  generateChart(grossR, netReturn)
}

const generateChart = (grossR, netR) => {
  const years = []
  const grossSeries = []
  const netSeries = []

  for (let y = 0; y <= formData.value.durationYears; y++) {
    years.push(`Year ${y}`)
    grossSeries.push(Math.round(Math.pow(1 + grossR, y) * 100) / 100)
    netSeries.push(Math.round(Math.pow(1 + netR, y) * 100) / 100)
  }

  chartData.value = {
    labels: years,
    datasets: [
      { label: 'Gross (1₹ growth)', data: grossSeries, borderColor: 'rgba(54,162,235,1)', backgroundColor: 'rgba(54,162,235,0.08)', fill: false, tension: 0.2 },
      { label: 'Net (after drag)', data: netSeries, borderColor: 'rgba(255,99,132,1)', backgroundColor: 'rgba(255,99,132,0.08)', fill: false, tension: 0.2 }
    ]
  }

  chartOptions.value = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { position: 'top' }, title: { display: true, text: 'Mutual Fund Drag: Gross vs Net Growth of ₹1' } },
    scales: { y: { beginAtZero: true } }
  }
}

// Initialize
calculate()
</script>

<style scoped>
.mutual-fund-drag-calculator { display:flex; flex-direction:column; gap:20px }
.calculator-grid { display:grid; grid-template-columns:1fr 1fr; gap:20px }
.form-fields { display:flex; flex-direction:column; gap:16px }
.field { display:flex; flex-direction:column; gap:8px }
.input-field { width:100% }
.calculate-btn { width:100% }
.result-item { padding:12px; background:var(--an-background-light); border-radius:8px; text-align:center }
.result-value { font-weight:700; font-size:1.1rem; margin-top:6px }
.chart-container { height:320px }
.chart { height:100% !important }
@media (max-width:768px) { .calculator-grid { grid-template-columns:1fr } .chart-container { height:240px } }
</style>
